import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Check, Package, Save, Loader2, ChevronLeft, ChevronRight, Zap } from 'lucide-react';
import { useBrand } from '../context/BrandContext';

const STORAGE_KEY = 'merchpie_default_categories';

// Curated list of popular product categories for the "Popular" tab
const POPULAR_CATEGORIES = [
  'unisex-t-shirt',
  'pullover-hoodie',
  'mug',
  'tote-bag',
  'poster',
  'phone-case',
  'crewneck-sweatshirt',
  'tank-top',
  'canvas-print',
  'sticker'
];

const ProductSelector = ({
  categories,
  selectedCategories,
  onSelectionChange,
  isLoading,
  blueprints = [],
  limits = null,  // { tier, products: { limits, usage, remaining } }
  onUpgradeClick = null
}) => {
  const [saveMessage, setSaveMessage] = useState('');
  const [activeCategory, setActiveCategory] = useState('popular');
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);
  const [showCategoryLeftArrow, setShowCategoryLeftArrow] = useState(false);
  const [showCategoryRightArrow, setShowCategoryRightArrow] = useState(true);
  const [showProductLeftArrow, setShowProductLeftArrow] = useState(false);
  const [showProductRightArrow, setShowProductRightArrow] = useState(true);

  const categoryScrollRef = useRef(null);
  const productScrollRef = useRef(null);
  const brand = useBrand();

  // Track if we've already loaded defaults to prevent overwriting pre-selections
  const hasLoadedDefaults = useRef(false);

  // Calculate effective maximum based on limits
  const effectiveMax = useMemo(() => {
    if (!limits?.products) {
      return 10; // Default if no limits
    }
    const { limits: tierLimits, remaining } = limits.products;
    return Math.min(
      tierLimits?.sessionMax || 10,
      remaining?.daily ?? Infinity,
      remaining?.monthly ?? Infinity
    );
  }, [limits]);

  // Determine which limit is constraining
  const limitConstraint = useMemo(() => {
    if (!limits?.products) return null;
    const { limits: tierLimits, remaining } = limits.products;

    if (remaining?.monthly === 0) return 'monthly';
    if (remaining?.daily === 0) return 'daily';
    if (selectedCategories.length >= (tierLimits?.sessionMax || 10)) return 'session';
    if (selectedCategories.length >= (remaining?.daily ?? Infinity)) return 'daily';
    if (selectedCategories.length >= (remaining?.monthly ?? Infinity)) return 'monthly';
    return null;
  }, [limits, selectedCategories.length]);

  // Load defaults from localStorage on mount - but only if no selection exists
  useEffect(() => {
    // Skip if we already loaded or if there's already a selection (e.g., from "Create Similar")
    if (hasLoadedDefaults.current || selectedCategories.length > 0) {
      hasLoadedDefaults.current = true;
      return;
    }

    const savedDefaults = localStorage.getItem(STORAGE_KEY);
    if (savedDefaults) {
      try {
        const defaults = JSON.parse(savedDefaults);
        if (Array.isArray(defaults) && defaults.length > 0) {
          onSelectionChange(defaults);
        }
      } catch (e) {
        console.error('Failed to load saved category defaults:', e);
      }
    }
    hasLoadedDefaults.current = true;
  }, [selectedCategories.length, onSelectionChange]);

  // Check scroll positions for arrow visibility
  const updateCategoryArrows = useCallback(() => {
    if (categoryScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = categoryScrollRef.current;
      setShowCategoryLeftArrow(scrollLeft > 10);
      setShowCategoryRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    }
  }, []);

  const updateProductArrows = useCallback(() => {
    if (productScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = productScrollRef.current;
      setShowProductLeftArrow(scrollLeft > 10);
      setShowProductRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    }
  }, []);

  // Update arrows on mount and when content changes
  useEffect(() => {
    updateCategoryArrows();
    updateProductArrows();
  }, [activeCategory, blueprints, updateCategoryArrows, updateProductArrows, showSelectedOnly, selectedCategories]);

  // Reset product scroll when category or filter changes
  useEffect(() => {
    if (productScrollRef.current) {
      productScrollRef.current.scrollLeft = 0;
      updateProductArrows();
    }
  }, [activeCategory, showSelectedOnly, updateProductArrows]);

  const scrollCategories = (direction) => {
    if (categoryScrollRef.current) {
      const scrollAmount = 200;
      categoryScrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
      setTimeout(updateCategoryArrows, 300);
    }
  };

  const scrollProducts = (direction) => {
    if (productScrollRef.current) {
      const scrollAmount = 400;
      productScrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
      setTimeout(updateProductArrows, 300);
    }
  };

  const toggleBlueprint = (blueprintId) => {
    const id = String(blueprintId);
    if (selectedCategories.includes(id)) {
      // Always allow deselection
      onSelectionChange(selectedCategories.filter(cid => cid !== id));
    } else {
      // Check if we can add more (block entirely if at limit)
      if (selectedCategories.length >= effectiveMax) {
        // At limit - don't add
        return;
      }
      onSelectionChange([...selectedCategories, id]);
    }
  };

  // Get popular blueprints - one from each popular category
  const getPopularBlueprints = useCallback(() => {
    const popular = [];
    const seenCategories = new Set();

    // First, get one blueprint from each popular category in order
    for (const catId of POPULAR_CATEGORIES) {
      const bp = blueprints.find(b => b.categoryId === catId && !seenCategories.has(b.categoryId));
      if (bp) {
        popular.push(bp);
        seenCategories.add(bp.categoryId);
      }
    }

    // Add more variety - get additional items from popular categories
    for (const catId of POPULAR_CATEGORIES) {
      const moreBps = blueprints.filter(b => b.categoryId === catId && !popular.includes(b));
      popular.push(...moreBps.slice(0, 2)); // Add up to 2 more from each category
    }

    return popular;
  }, [blueprints]);

  const getFilteredBlueprints = useCallback(() => {
    // If showing selected only, filter to just selected blueprints
    if (showSelectedOnly) {
      return blueprints.filter(bp => selectedCategories.includes(String(bp.id)));
    }

    if (activeCategory === 'popular') {
      return getPopularBlueprints();
    }
    return blueprints.filter(bp => bp.categoryId === activeCategory);
  }, [activeCategory, blueprints, getPopularBlueprints, showSelectedOnly, selectedCategories]);

  const clearAll = () => {
    onSelectionChange([]);
    setShowSelectedOnly(false); // Reset filter when clearing
  };

  const saveAsDefault = () => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(selectedCategories));
      setSaveMessage('Saved!');
      setTimeout(() => setSaveMessage(''), 2000);
    } catch (e) {
      console.error('Failed to save defaults:', e);
      setSaveMessage('Failed');
      setTimeout(() => setSaveMessage(''), 2000);
    }
  };

  const popularBlueprints = getPopularBlueprints();
  const filteredBlueprints = getFilteredBlueprints();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: brand.colors.primary }} />
        <span className="ml-3 text-slate-600">Loading products...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-bold text-slate-900">Choose Your Products</h3>
          <p className="text-base text-slate-500">
            {selectedCategories.length} product{selectedCategories.length !== 1 ? 's' : ''} selected
          </p>
        </div>
        <button
          onClick={saveAsDefault}
          disabled={selectedCategories.length === 0}
          className="px-4 py-2 text-base font-medium rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ color: brand.colors.primary }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = `${brand.colors.primary}10`; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = ''; }}
        >
          <Save className="w-5 h-5" />
          {saveMessage || 'Save Default'}
        </button>
      </div>

      {/* Category Navigation - Horizontal Scrollable */}
      <div className="relative flex items-center">
        {/* Left Arrow */}
        <button
          onClick={() => scrollCategories('left')}
          className={`absolute left-0 z-10 w-8 h-8 bg-white shadow-md rounded-full flex items-center justify-center text-slate-600 hover:text-slate-900 hover:shadow-lg transition-all ${
            showCategoryLeftArrow ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        {/* Scrollable Categories */}
        <div
          ref={categoryScrollRef}
          onScroll={updateCategoryArrows}
          className="flex gap-2 overflow-x-auto px-10 py-1 scrollbar-hide"
          style={{ scrollBehavior: 'smooth' }}
        >
          <button
            onClick={() => setActiveCategory('popular')}
            className={`whitespace-nowrap px-4 py-2 rounded-lg text-base font-semibold transition-all flex-shrink-0
              ${activeCategory === 'popular'
                ? 'text-white shadow-md'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            style={activeCategory === 'popular' ? { backgroundColor: brand.colors.primary } : {}}
          >
            Popular ({popularBlueprints.length})
          </button>
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`whitespace-nowrap px-4 py-2 rounded-lg text-base font-semibold transition-all flex-shrink-0
                ${activeCategory === cat.id
                  ? 'text-white shadow-md'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              style={activeCategory === cat.id ? { backgroundColor: brand.colors.primary } : {}}
            >
              {cat.name} ({cat.blueprintCount})
            </button>
          ))}
        </div>

        {/* Right Arrow */}
        <button
          onClick={() => scrollCategories('right')}
          className={`absolute right-0 z-10 w-8 h-8 bg-white shadow-md rounded-full flex items-center justify-center text-slate-600 hover:text-slate-900 hover:shadow-lg transition-all ${
            showCategoryRightArrow ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Product Carousel - Horizontal Scrollable */}
      <div className="relative flex items-center py-2">
        {/* Left Arrow */}
        <button
          onClick={() => scrollProducts('left')}
          className={`absolute left-0 z-10 w-10 h-10 bg-white shadow-lg rounded-full flex items-center justify-center text-slate-600 hover:text-slate-900 hover:shadow-xl transition-all ${
            showProductLeftArrow ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        >
          <ChevronLeft className="w-6 h-6" />
        </button>

        {/* Scrollable Products */}
        <div
          ref={productScrollRef}
          onScroll={updateProductArrows}
          className="flex gap-4 overflow-x-auto px-14 py-3 scrollbar-hide"
          style={{ scrollBehavior: 'smooth', scrollSnapType: 'x mandatory' }}
        >
          {filteredBlueprints.map((blueprint) => {
            const isSelected = selectedCategories.includes(String(blueprint.id));
            return (
              <button
                key={blueprint.id}
                onClick={() => toggleBlueprint(blueprint.id)}
                className={`relative flex-shrink-0 w-[160px] rounded-xl border-2 transition-all duration-150 overflow-hidden group text-left
                  ${isSelected
                    ? 'shadow-lg'
                    : 'border-slate-200 bg-white hover:shadow-md'
                  }
                `}
                style={{
                  scrollSnapAlign: 'start',
                  ...(isSelected
                    ? { borderColor: brand.colors.primary, boxShadow: `0 0 0 2px ${brand.colors.primary}30` }
                    : {}
                  )
                }}
                onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.borderColor = `${brand.colors.primary}60`; }}
                onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.borderColor = ''; }}
              >
                {/* Selection indicator */}
                <div
                  className={`absolute top-2 right-2 z-10 w-6 h-6 rounded-full flex items-center justify-center transition-all
                    ${isSelected
                      ? 'text-white shadow'
                      : 'bg-white/90 border border-slate-300'
                    }
                  `}
                  style={isSelected ? { backgroundColor: brand.colors.primary } : {}}
                >
                  {isSelected && <Check className="w-4 h-4" />}
                </div>

                {/* Product Image */}
                <div className="aspect-square bg-white relative overflow-hidden">
                  {blueprint.image ? (
                    <img
                      src={blueprint.image}
                      alt={blueprint.title}
                      className="w-full h-full object-contain p-1"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-slate-50">
                      <Package className="w-12 h-12 text-slate-300" />
                    </div>
                  )}
                  {isSelected && (
                    <div className="absolute inset-0" style={{ backgroundColor: `${brand.colors.primary}18` }} />
                  )}
                </div>

                {/* Product Info */}
                <div
                  className={`p-2 border-t ${!isSelected ? 'bg-slate-50 border-slate-100' : ''}`}
                  style={isSelected ? { backgroundColor: `${brand.colors.primary}10`, borderColor: `${brand.colors.primary}20` } : {}}
                >
                  <h4
                    className="font-semibold text-xs leading-tight line-clamp-2"
                    style={{ color: isSelected ? (brand.colors.primaryDark || brand.colors.primary) : undefined }}>
                    {blueprint.title}
                  </h4>
                </div>
              </button>
            );
          })}
        </div>

        {/* Right Arrow */}
        <button
          onClick={() => scrollProducts('right')}
          className={`absolute right-0 z-10 w-10 h-10 bg-white shadow-lg rounded-full flex items-center justify-center text-slate-600 hover:text-slate-900 hover:shadow-xl transition-all ${
            showProductRightArrow ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        >
          <ChevronRight className="w-6 h-6" />
        </button>
      </div>

      {/* Empty state */}
      {blueprints.length === 0 && !isLoading && (
        <div className="text-center py-12 text-slate-500">
          <Package className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p>No products available</p>
        </div>
      )}

      {/* Empty state for "Show Selected" when no matches found */}
      {showSelectedOnly && filteredBlueprints.length === 0 && selectedCategories.length > 0 && (
        <div className="text-center py-8 text-slate-500">
          <Package className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          <p className="text-sm">Selected product not found in catalog</p>
          <button
            onClick={() => setShowSelectedOnly(false)}
            className="mt-2 text-sm font-medium"
            style={{ color: brand.colors.primary }}
            onMouseEnter={(e) => { e.target.style.color = brand.colors.primaryHover || brand.colors.primary; }}
            onMouseLeave={(e) => { e.target.style.color = brand.colors.primary; }}
          >
            Show all products
          </button>
        </div>
      )}

      {/* Selection summary */}
      {selectedCategories.length > 0 && (
        <div
          className="rounded-lg p-3 flex items-center justify-between border"
          style={{ backgroundColor: `${brand.colors.primary}10`, borderColor: `${brand.colors.primary}30` }}
        >
          <p className="font-medium text-sm" style={{ color: brand.colors.primaryDark || brand.colors.primary }}>
            {selectedCategories.length} product{selectedCategories.length !== 1 ? 's' : ''} selected
            {limits?.products && (
              <span className="text-slate-500 ml-2">
                (max {effectiveMax} per {limitConstraint === 'session' ? 'session' : limitConstraint === 'daily' ? 'day' : 'month'})
              </span>
            )}
          </p>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowSelectedOnly(!showSelectedOnly)}
              className="text-xs font-medium px-2 py-1 rounded transition-colors"
              style={showSelectedOnly
                ? { backgroundColor: brand.colors.primary, color: 'white' }
                : { color: brand.colors.primary }
              }
              onMouseEnter={(e) => { if (!showSelectedOnly) { e.target.style.backgroundColor = `${brand.colors.primary}15`; } }}
              onMouseLeave={(e) => { if (!showSelectedOnly) { e.target.style.backgroundColor = ''; } }}
            >
              {showSelectedOnly ? 'Show All' : 'Show Selected'}
            </button>
            <button
              onClick={clearAll}
              className="text-xs font-medium"
              style={{ color: brand.colors.primary }}
              onMouseEnter={(e) => { e.target.style.color = brand.colors.primaryHover || brand.colors.primary; }}
              onMouseLeave={(e) => { e.target.style.color = brand.colors.primary; }}
            >
              Clear all
            </button>
          </div>
        </div>
      )}

      {/* Product limit reached warning */}
      {selectedCategories.length >= effectiveMax && limits?.products && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span>
              {limitConstraint === 'daily'
                ? `Daily limit reached (${limits.products.usage.daily}/${limits.products.limits.dailyMax} products).`
                : limitConstraint === 'monthly'
                  ? `Monthly limit reached (${limits.products.usage.monthly}/${limits.products.limits.monthlyMax} products).`
                  : `Maximum ${effectiveMax} products per session.`
              }
            </span>
          </div>
          {limits?.tier !== 'enterprise' && onUpgradeClick && brand?.features?.limits?.showUpgradePrompts !== false && (
            <button
              onClick={onUpgradeClick}
              className="flex items-center gap-1 px-3 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-xs font-medium transition-colors"
            >
              <Zap size={14} />
              Upgrade
            </button>
          )}
        </div>
      )}

      {/* Selection warning */}
      {selectedCategories.length === 0 && blueprints.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700 flex items-center gap-2">
          <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <span>Select at least one product to continue</span>
        </div>
      )}

      {/* Hidden scrollbar styles */}
      <style>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default ProductSelector;
